import { AxiosResponse } from 'axios';
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from 'redux-saga/effects';
import { $alerts } from '@pickit/pickit-components';
import i18next from '../../config/i18next';
import { actions } from '../../redux/login/actions';
import { actions as forgotPasswordActions } from '../../redux/forgotPassword/actions';
import * as LoginService from '../../services/login/login';
import { LoginResponseType } from '../../services/login/types';

// eslint-disable-next-line consistent-return
export function* login(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<LoginResponseType>>
  | PutEffect<{ type: string }>
  | CallEffect<void>,
  void,
  LoginResponseType
> {
  const response = yield call(LoginService.login, {
    email: action.payload.email,
    password: action.payload.password,
    applicationName: action.payload.applicationName,
  });
  switch (response.status) {
    case 500:
      yield put(actions.getLoginError(false));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
    case 400:
      yield put(actions.getLoginError(true));
      if (response.data.code === 20035) {
        yield put(
          forgotPasswordActions.getForgotPasswordRequest({
            email: action.payload.email,
            callbackUrl: action.payload.callbackUrl,
            applicationName: action.payload.applicationName,
            modalInfo: true,
          }),
        );
      } else {
        $alerts.next({
          type: 'error',
          title: i18next.t(`LoginService:errorCode${response.data.code}`),
          subMessage: i18next.t(
            `LoginService:errorCode${response.data.code}Body`,
          ),
          onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
        });
      }
      break;
    case 401:
      yield put(actions.getLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });

    case 403:
      yield put(actions.getLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });

    case 404:
      yield put(actions.getLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });

    case 429:
      yield put(actions.getLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
    case 200:
      yield put(actions.getLoginSuccess(response.data));
      action.payload.onSuccess(response.data);
      break;
    case 201:
      yield put(actions.getLoginSuccess(response.data));
      action.payload.onSuccess(response.data);
      break;
    default:
      yield put(actions.getLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t('LoginService:errorGeneralTitle'),
        subMessage: i18next.t('LoginService:errorGeneralBody'),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
  }
}
// eslint-disable-next-line consistent-return
// eslint-disable-next-line consistent-return
export function* loginWithGoogle(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<LoginResponseType>>
  | PutEffect<{ type: string }>
  | CallEffect<void>,
  void,
  LoginResponseType
> {
  const response = yield call(LoginService.loginWithGoogle, {
    googleToken: action.payload.googleToken,
    applicationName: action.payload.applicationName,
  });
  switch (response.status) {
    case 500:
      yield put(actions.getGoogleLoginError(false));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
    case 400:
      yield put(actions.getGoogleLoginError(true));
      $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
      break;
    case 401:
      yield put(actions.getGoogleLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
    case 403:
      yield put(actions.getGoogleLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
    case 404:
      yield put(actions.getGoogleLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t(`LoginService:errorCode${response.data.code}`),
        subMessage: i18next.t(
          `LoginService:errorCode${response.data.code}Body`,
        ),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
    case 200:
      yield put(actions.getGoogleLoginSuccess(response.data));
      action.payload.onSuccess(response.data);
      break;
    case 201:
      yield put(actions.getGoogleLoginSuccess(response.data));
      action.payload.onSuccess(response.data);
      break;
    default:
      yield put(actions.getGoogleLoginError(true));
      return $alerts.next({
        type: 'error',
        title: i18next.t('LoginService:errorGeneralTitle'),
        subMessage: i18next.t('LoginService:errorGeneralBody'),
        onConfirmLabel: i18next.t(`LoginService:errorModalButton`),
      });
  }
}

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getLoginRequest.type, login),
  takeLatest(actions.getGoogleLoginRequest.type, loginWithGoogle),
];

export default sagas;
